import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Car } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../api/client'
import { useAuth } from '../context/AuthContext'

export default function RegisterPage() {
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const googleBtnRef = useRef(null)

  const [form, setForm]       = useState({ name: '', email: '', password: '' })
  const [showPw, setShowPw]   = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) navigate('/', { replace: true })
  }, [user, navigate])

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId || !window.google) return

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback:  handleGoogleCredential,
    })

    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme:  'filled_black',
      size:   'large',
      width:  '100%',
      text:   'signup_with',
      shape:  'rectangular',
    })
  }, [])

  const handleGoogleCredential = async ({ credential }) => {
    setLoading(true)
    try {
      const { data } = await api.post('/auth/google', { credential })
      login(data.accessToken, data.user)
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Google sign-up failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.post('/auth/register', form)
      login(data.accessToken, data.user)
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-[var(--bg-base)] p-6">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center shadow-glow">
            <Car size={15} className="text-white" />
          </div>
          <span className="font-display font-bold text-[var(--text-primary)]">Service Tracker</span>
        </div>

        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] mb-2">
          Create an account
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mb-8">
          Get started tracking your vehicles and service history.
        </p>

        {/* Google SSO */}
        <div ref={googleBtnRef} className="mb-4" />

        {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-xs text-[var(--text-muted)]">or</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Full name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
              required
              className="w-full px-3 py-2 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                         rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                         focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              required
              className="w-full px-3 py-2 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                         rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                         focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Password
              <span className="ml-1 font-normal text-[var(--text-muted)]">(min. 8 characters)</span>
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                required
                className="w-full px-3 py-2 pr-10 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                           rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                           focus:outline-none focus:border-indigo-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]
                           hover:text-[var(--text-secondary)] transition-colors"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[var(--text-secondary)]">
          Already have an account?{' '}
          <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
